import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Wand2 } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { ROUTES, API_ENDPOINTS } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';
import api from '@services/api';
import { useRecaptcha } from '@hooks/useRecaptcha';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    acceptTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);

  const { isAuthenticated, error } = useAuth();
  const { getToken } = useRecaptcha();
  const navigate = useNavigate();

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.AUTH_OAUTH_PROVIDERS);
        setOauthProviders(response.data.providers || []);
      } catch {
        setOauthProviders([]);
      }
    };
    fetchProviders();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
    setSuccessMessage(null);
  };

  const validateForm = (): boolean => {
    if (!formData.first_name.trim()) {
      setFormError('First name is required');
      return false;
    }

    if (formData.first_name.trim().length < 1) {
      setFormError('First name must be at least 1 character');
      return false;
    }

    if (!formData.last_name.trim()) {
      setFormError('Last name is required');
      return false;
    }

    if (formData.last_name.trim().length < 1) {
      setFormError('Last name must be at least 1 character');
      return false;
    }

    if (!formData.email.trim()) {
      setFormError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return false;
    }

    if (!formData.acceptTerms) {
      setFormError('You must accept the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setFormError(null);

    try {
      // Register user without password (passwordless)
      const captchaToken = await getToken('register');
      await api.post(API_ENDPOINTS.AUTH_REGISTER_PASSWORDLESS, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: 'photographer',
        captcha_token: captchaToken,
      });

      setSuccessMessage(
        'Account created! Check your email for a magic link to complete your registration and sign in.'
      );
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Registration failed';
      setFormError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthRegister = async (provider: 'google' | 'github') => {
    try {
      const endpoint =
        provider === 'google'
          ? API_ENDPOINTS.AUTH_OAUTH_GOOGLE
          : API_ENDPOINTS.AUTH_OAUTH_GITHUB;

      const response = await api.get(endpoint);
      const { authorization_url } = response.data;

      window.location.href = authorization_url;
    } catch {
      setFormError(`Failed to initiate ${provider} registration`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create your photographer account</h2>
            <p className="text-gray-600">Start selling your event photos on Pixel Share</p>
          </div>

          {/* Registration Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Passwordless Info */}
            <div className="flex items-center justify-center mb-6 text-primary-600">
              <Wand2 size={20} className="mr-2" />
              <span className="text-sm font-medium">Passwordless Registration</span>
            </div>

            {successMessage && (
              <Alert
                type="success"
                message={successMessage}
                className="mb-4"
                dismissible
                onDismiss={() => setSuccessMessage(null)}
              />
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  label="First Name"
                  value={formData.first_name}
                  onChange={e => handleInputChange('first_name', e.target.value)}
                  leftIcon={<User size={16} />}
                  placeholder="First name"
                  required
                  autoComplete="given-name"
                />
                <Input
                  type="text"
                  label="Last Name"
                  value={formData.last_name}
                  onChange={e => handleInputChange('last_name', e.target.value)}
                  leftIcon={<User size={16} />}
                  placeholder="Last name"
                  required
                  autoComplete="family-name"
                />
              </div>

              <Input
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                leftIcon={<Mail size={16} />}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />

              <p className="text-sm text-gray-500 text-center">
                We'll send you a magic link to verify your email and sign in - no password needed!
              </p>

              <div className="flex items-start">
                <input
                  id="accept-terms"
                  name="accept-terms"
                  type="checkbox"
                  checked={formData.acceptTerms}
                  onChange={e => handleInputChange('acceptTerms', e.target.checked)}
                  className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="accept-terms" className="ml-3 block text-sm text-gray-700">
                  I agree to the{' '}
                  <Link to="#" className="text-primary-600 hover:text-primary-500">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="#" className="text-primary-600 hover:text-primary-500">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {(formError || error) && (
                <Alert
                  type="error"
                  message={formError || error || ''}
                  dismissible
                  onDismiss={() => setFormError(null)}
                />
              )}

              <Button
                type="submit"
                variant="primary"
                size="large"
                className="w-full"
                loading={isLoading}
                disabled={isLoading}
              >
                Create Account
              </Button>
            </form>
          </div>

          {/* Sign in link */}
          <div className="text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link
                to={ROUTES.LOGIN}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Social registration */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or sign up with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthRegister('google')}
                disabled={!oauthProviders.includes('google')}
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20">
                  <path
                    fill="currentColor"
                    d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"
                  />
                </svg>
                <span className="ml-2">Google</span>
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthRegister('github')}
                disabled={!oauthProviders.includes('github')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 0C4.477 0 0 4.477 0 10c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.341-3.369-1.341-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0110 4.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C17.138 18.163 20 14.418 20 10c0-5.523-4.477-10-10-10z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-2">GitHub</span>
              </Button>
            </div>
            {oauthProviders.length === 0 && (
              <p className="text-xs text-gray-400 text-center mt-2">
                OAuth providers not configured
              </p>
            )}
          </div>

          {/* Benefits */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-primary-800 mb-2">
              Sell your photos with Pixel Share:
            </h3>
            <ul className="text-xs text-primary-700 space-y-1">
              <li>• Your own branded gallery micro-site</li>
              <li>• Set your own price sheets per event</li>
              <li>• Secure checkout and payouts to your bank</li>
              <li>• Keep the majority of every sale</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterPage;
